import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Copy, CheckCircle2, Mail, Building2, Hash, Clock } from "lucide-react";
import { toast } from "sonner";
import { api, fmt } from "@/lib/api";
import { useCart } from "@/context/CartContext";

const CopyRow = ({ label, value, testid }) => {
  const [copied, setCopied] = useState(false);
  if (!value) return null;
  const copy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    toast.success(`${label} copied`);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button
      type="button"
      onClick={copy}
      data-testid={testid}
      className="w-full flex items-center justify-between gap-4 px-4 py-3 border border-white/10 hover:border-cyan-500/40 transition text-left group"
    >
      <div className="min-w-0">
        <div className="label-mono text-slate-500 text-[10px]">{label}</div>
        <div className="font-mono text-base text-cyan-300 truncate">{value}</div>
      </div>
      <div className="text-slate-500 group-hover:text-cyan-400 shrink-0">
        {copied ? <CheckCircle2 size={16} /> : <Copy size={16} />}
      </div>
    </button>
  );
};

export default function CheckoutBankTransfer() {
  const { reference } = useParams();
  const [order, setOrder] = useState(null);
  const [methods, setMethods] = useState(null);
  const { clear } = useCart();

  useEffect(() => {
    api.get(`/orders/by-reference/${reference}`).then((r) => setOrder(r.data));
    api.get("/payment-methods").then((r) => {
      const bt = r.data.methods.find((m) => m.id === "bank_transfer");
      setMethods(bt?.details || null);
    });
    // Clear cart once user lands on instructions
    clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reference]);

  if (!order) {
    return <div className="max-w-3xl mx-auto px-6 py-20 text-slate-400 text-center">Loading…</div>;
  }

  // Order already paid (e.g. via PayPal) — show a confirmation view instead
  // of bank transfer instructions.
  if (order.status === "paid") {
    return (
      <div className="max-w-3xl mx-auto px-6 lg:px-10 py-12" data-testid="checkout-paid">
        <div className="label-mono text-cyan-400 mb-3">/ PAYMENT RECEIVED</div>
        <h1 className="text-4xl md:text-5xl font-medium tracking-tight leading-tight">
          Thank you.<br/>
          <span className="text-cyan-400">Your order is confirmed.</span>
        </h1>

        <div className="mt-8 grid sm:grid-cols-2 gap-3">
          <div className="border border-cyan-500/30 bg-cyan-500/5 p-5">
            <div className="label-mono text-slate-400 text-[10px]">Amount paid</div>
            <div className="font-mono text-3xl text-cyan-400 mt-1" data-testid="paid-amount">{fmt(order.total)}</div>
          </div>
          <div className="border border-cyan-500/30 bg-cyan-500/5 p-5">
            <div className="label-mono text-slate-400 text-[10px]">Order reference</div>
            <div className="font-mono text-2xl text-cyan-400 mt-1.5" data-testid="paid-reference">{order.reference}</div>
          </div>
        </div>

        <div className="mt-10 p-5 border border-white/10 flex gap-4">
          <Mail className="text-cyan-400 shrink-0" size={20} />
          <div>
            <div className="font-medium">What happens next</div>
            <p className="text-sm text-slate-400 mt-1 leading-relaxed">
              A receipt has been emailed to <span className="font-mono text-cyan-400">{order.email}</span>. Your order will be dispatched within 48 hours via tracked cold-chain courier — tracking will follow by email.
            </p>
          </div>
        </div>

        <div className="mt-10 flex flex-wrap gap-3">
          <Link to="/account" data-testid="view-orders-link" className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold px-6 py-3">View my orders</Link>
          <Link to="/shop" className="border border-cyan-500/30 text-cyan-400 px-6 py-3 hover:bg-cyan-500/10">Continue shopping</Link>
        </div>
      </div>
    );
  }

  // Bank transfer flow — needs methods (PayID/BSB details) before rendering.
  if (!methods) {
    return <div className="max-w-3xl mx-auto px-6 py-20 text-slate-400 text-center">Loading…</div>;
  }

  return (
    <div className="max-w-3xl mx-auto px-6 lg:px-10 py-12" data-testid="bank-transfer-instructions">
      <div className="label-mono text-cyan-400 mb-3">/ AWAITING PAYMENT</div>
      <h1 className="text-4xl md:text-5xl font-medium tracking-tight leading-tight">
        Your order is reserved.<br/>
        <span className="text-cyan-400">Complete payment to ship.</span>
      </h1>

      <div className="mt-8 p-5 border border-amber-500/30 bg-amber-500/5">
        <div className="flex items-center gap-2 label-mono text-amber-400">
          <Clock size={14} /> Pay within 48 hours
        </div>
        <p className="mt-2 text-sm text-amber-100/80 leading-relaxed">
          Your order is held for 48 hours. Once we confirm your bank transfer, we&apos;ll dispatch within 48h and email you tracking. Orders unpaid after 48h are released.
        </p>
      </div>

      {/* Key amount + reference (most important) */}
      <div className="mt-8 grid sm:grid-cols-2 gap-3">
        <div className="border border-cyan-500/30 bg-cyan-500/5 p-5">
          <div className="label-mono text-slate-400 text-[10px]">Amount to send</div>
          <div className="font-mono text-3xl text-cyan-400 mt-1" data-testid="bt-amount">{fmt(order.total)}</div>
        </div>
        <div className="border border-cyan-500/30 bg-cyan-500/5 p-5">
          <div className="label-mono text-slate-400 text-[10px]">Reference (paste in description)</div>
          <div className="font-mono text-2xl text-cyan-400 mt-1.5" data-testid="bt-reference">{order.reference}</div>
        </div>
      </div>

      <p className="mt-6 text-sm text-slate-300 leading-relaxed">
        Open your banking app and send <span className="font-mono text-cyan-400">{fmt(order.total)}</span> using <strong className="text-white">PayID (instant, recommended)</strong> or to the bank account below. <strong className="text-white">You must include the reference</strong> <span className="font-mono text-cyan-400">{order.reference}</span> in the payment description so we can match it to your order.
      </p>

      {/* PayID block */}
      {methods.payid && (
        <div className="mt-8">
          <div className="label-mono text-cyan-400 mb-3 flex items-center gap-2">
            <Hash size={12} /> Option A · PayID (instant)
          </div>
          <div className="space-y-3">
            <CopyRow label="PayID" value={methods.payid} testid="copy-payid" />
            {methods.account_name && <CopyRow label="Account name (verify in your app)" value={methods.account_name} testid="copy-account-name" />}
            {methods.bank && <CopyRow label="Bank" value={methods.bank} testid="copy-bank" />}
          </div>
        </div>
      )}

      {/* BSB / Account block */}
      {(methods.bsb || methods.account_number) && (
        <div className="mt-8">
          <div className="label-mono text-cyan-400 mb-3 flex items-center gap-2">
            <Building2 size={12} /> Option B · BSB &amp; Account (1-2 business days)
          </div>
          <div className="space-y-3">
            {methods.bsb && <CopyRow label="BSB" value={methods.bsb} testid="copy-bsb" />}
            {methods.account_number && <CopyRow label="Account number" value={methods.account_number} testid="copy-acc" />}
            {methods.bank && <CopyRow label="Bank" value={methods.bank} testid="copy-bank-b" />}
          </div>
        </div>
      )}

      {/* Confirmation note */}
      <div className="mt-10 p-5 border border-white/10 flex gap-4">
        <Mail className="text-cyan-400 shrink-0" size={20} />
        <div>
          <div className="font-medium">What happens next</div>
          <p className="text-sm text-slate-400 mt-1 leading-relaxed">
            We typically confirm payments within a few hours (or instantly for PayID). You&apos;ll receive a confirmation email at <span className="font-mono text-cyan-400">{order.email}</span> when funds are received. Your order will then be dispatched within 48 hours via tracked cold-chain courier.
          </p>
        </div>
      </div>

      <div className="mt-10 flex flex-wrap gap-3">
        <Link to="/account" data-testid="view-orders-link" className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold px-6 py-3">View my orders</Link>
        <Link to="/shop" className="border border-cyan-500/30 text-cyan-400 px-6 py-3 hover:bg-cyan-500/10">Continue shopping</Link>
      </div>

      <p className="mt-10 text-xs text-slate-500 leading-relaxed">
        Questions? Email <a href="mailto:orders@novagenixlabs.store" className="text-cyan-400">orders@novagenixlabs.store</a> with your reference number.
      </p>
    </div>
  );
}
